import { useState, useEffect, useCallback } from 'react';
import {
  Activity, Camera, ChevronDown, Eye, LockKeyhole,
  Plus, Printer, Save, Search, ShieldCheck, Trash2, X,
} from 'lucide-react';
import { saveDentalNote, loadClientVisitHistory, loadDentalServices } from './data.js';
import { PhotoModal } from './PhotoModal.jsx';
import { ageFromBirthDate, dateLabel, friendlyError } from './lib/format.js';

// ── ICD-10 Dental ─────────────────────────────────────────────────────────────
const ICD10_DENTAL = [
  { code: 'K00.0', display: 'Anodontia' },
  { code: 'K00.1', display: 'Supernumerary teeth' },
  { code: 'K00.6', display: 'Disturbances in tooth eruption' },
  { code: 'K01.0', display: 'Embedded teeth' },
  { code: 'K01.1', display: 'Impacted teeth' },
  { code: 'K02.0', display: 'Caries enamel' },
  { code: 'K02.1', display: 'Caries dentine' },
  { code: 'K02.2', display: 'Caries cementum' },
  { code: 'K02.9', display: 'Dental caries, unspecified' },
  { code: 'K03.0', display: 'Excessive attrition' },
  { code: 'K03.6', display: 'Calculus / deposits on teeth' },
  { code: 'K04.0', display: 'Pulpitis' },
  { code: 'K04.1', display: 'Necrosis of pulp' },
  { code: 'K04.4', display: 'Acute apical periodontitis' },
  { code: 'K04.5', display: 'Chronic apical periodontitis' },
  { code: 'K04.7', display: 'Periapical abscess without sinus' },
  { code: 'K05.0', display: 'Acute gingivitis' },
  { code: 'K05.1', display: 'Chronic gingivitis' },
  { code: 'K05.2', display: 'Acute periodontitis' },
  { code: 'K05.3', display: 'Chronic periodontitis' },
  { code: 'K06.1', display: 'Gingival enlargement' },
  { code: 'K06.8', display: 'Other disorders of gingiva' },
  { code: 'K08.1', display: 'Loss of teeth' },
  { code: 'K08.8', display: 'Other disorders of teeth' },
  { code: 'K09.0', display: 'Developmental odontogenic cysts' },
  { code: 'K12.0', display: 'Recurrent oral aphthae' },
  { code: 'K12.1', display: 'Other forms of stomatitis' },
  { code: 'Z01.2', display: 'Dental examination' },
];

// ── Odontogram constants ──────────────────────────────────────────────────────
const UPPER_PERMANENT  = [18,17,16,15,14,13,12,11, 21,22,23,24,25,26,27,28];
const LOWER_PERMANENT  = [48,47,46,45,44,43,42,41, 31,32,33,34,35,36,37,38];
const UPPER_DECIDUOUS  = [55,54,53,52,51, 61,62,63,64,65];
const LOWER_DECIDUOUS  = [85,84,83,82,81, 71,72,73,74,75];

const TOOTH_CONDITIONS = [
  { value: '',    label: '— Pilih kondisi —' },
  { value: 'AMF', label: 'AMF – Amalgam' },
  { value: 'COF', label: 'COF – Komposit' },
  { value: 'FIS', label: 'FIS – Fissure Sealant' },
  { value: 'GIC', label: 'GIC – Glass Ionomer' },
  { value: 'NVT', label: 'NVT – Non Vital' },
  { value: 'RCT', label: 'RCT – Root Canal Treatment' },
  { value: 'MIS', label: 'MIS – Missing' },
  { value: 'UNE', label: 'UNE – Unerupted' },
  { value: 'PRE', label: 'PRE – Partial Eruption' },
  { value: 'ANO', label: 'ANO – Anomali' },
  { value: 'CAR', label: 'CAR – Karies' },
  { value: 'FRC', label: 'FRC – Fraktur' },
  { value: 'IPX', label: 'IPX – Impaksi' },
  { value: 'RRX', label: 'RRX – Sisa Akar' },
  { value: 'POC', label: 'POC – Pontic' },
  { value: 'CRW', label: 'CRW – Crown' },
  { value: 'VEN', label: 'VEN – Veneer' },
  { value: 'WNG', label: 'WNG – Wing' },
];

const SURFACE_OPTS = ['Seluruh','Mesial','Distal','Bukal/Labial','Palatal/Lingual','Oklusal/Insisal'];

const OKLUSI_OPTS   = ['Normal Bite','Protrusif','Deep Bite','Cross Bite','Open Bite','Edge to Edge'];
const PALATUM_OPTS  = ['Dalam','Sedang','Dangkal'];
const TORUS_OPTS    = ['Tidak ada','Ada – kecil','Ada – besar'];
const DIASTEMA_OPTS = ['Tidak ada','Ada'];

const CONDITION_COLOR = {
  CAR: '#ef4444', RCT: '#a855f7', NVT: '#6b7280', MIS: '#1f2937',
  FRC: '#f97316', IPX: '#0ea5e9', RRX: '#92400e', AMF: '#64748b',
  COF: '#0369a1', GIC: '#059669', POC: '#d97706', CRW: '#7c3aed',
  VEN: '#db2777', UNE: '#9ca3af', PRE: '#fbbf24', ANO: '#dc2626',
  FIS: '#34d399', WNG: '#818cf8',
};

const HIST_TABS = ['Riwayat','Vital','Alergi','Odontogram','Terapi','Berkas'];

function nullable(v) { return v ?? ''; }

function normalizeProcedureList(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  return [];
}

// ── ICD-10 Picker (Dental) ─────────────────────────────────────────────────────
function ICD10Picker({ value, onChange, multiple = false, readOnly, placeholder }) {
  const [query, setQuery] = useState('');
  const [open, setOpen]   = useState(false);
  const filtered = ICD10_DENTAL.filter((item) => {
    const q = query.toLowerCase();
    return !q || item.code.toLowerCase().includes(q) || item.display.toLowerCase().includes(q);
  }).slice(0, 12);
  const tags = multiple ? (Array.isArray(value) ? value : []) : (value ? [value] : []);

  function select(item) {
    if (multiple) {
      const cur = Array.isArray(value) ? value : [];
      if (!cur.find((r) => r.code === item.code)) onChange([...cur, item]);
    } else { onChange(item); }
    setQuery(''); setOpen(false);
  }
  function remove(code) {
    if (multiple) onChange((Array.isArray(value) ? value : []).filter((r) => r.code !== code));
    else onChange(null);
  }

  return (
    <div className="icd-picker">
      {tags.length ? (
        <div className="icd-tags">
          {tags.map((r) => (
            <span key={r.code} className="icd-tag">
              <code>{r.code}</code> {r.display}
              {!readOnly && <button type="button" className="icd-tag-remove" onClick={() => remove(r.code)}>×</button>}
            </span>
          ))}
        </div>
      ) : null}
      {!readOnly && (!value || multiple) ? (
        <div className="icd-search-wrap">
          <Search size={13} className="icd-search-icon" />
          <input
            className="icd-search-input"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 180)}
            placeholder={placeholder || 'Cari kode ICD-10 dental…'}
          />
          {open && filtered.length ? (
            <div className="icd-dropdown">
              {filtered.map((item) => (
                <div key={item.code} className="icd-option" onMouseDown={() => select(item)}>
                  <code>{item.code}</code><span>{item.display}</span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ── Tooth SVG — 5 permukaan klinis ───────────────────────────────────────────
function ToothIcon({ conditions = [], size = 42 }) {
  const sc = {};
  conditions.forEach(({ condition, surface }) => {
    if (!condition) return;
    const col = CONDITION_COLOR[condition] || '#0369a1';
    const s   = surface || 'Seluruh';
    if (s === 'Seluruh') { ['B','L','M','D','O'].forEach((k) => { sc[k] = col; }); }
    else if (s.startsWith('Mesial'))                             { sc['M'] = col; }
    else if (s.startsWith('Distal'))                             { sc['D'] = col; }
    else if (s.startsWith('Bukal') || s.startsWith('Labial'))   { sc['B'] = col; }
    else if (s.startsWith('Palatal') || s.startsWith('Lingual')){ sc['L'] = col; }
    else if (s.startsWith('Oklusal') || s.startsWith('Insisal')){ sc['O'] = col; }
  });
  const first     = conditions.find((c) => c.condition);
  const mainColor = first ? (CONDITION_COLOR[first.condition] || '#0369a1') : null;
  const border    = mainColor || '#c4c9d4';
  const line      = '#bdc3cc';
  const f = (k) => sc[k] || (mainColor ? mainColor + '18' : '#fff');

  return (
    <svg width={size} height={size} viewBox="0 0 42 42" fill="none" xmlns="http://www.w3.org/2000/svg">
      <polygon points="1,1 41,1 31,13 11,13"   fill={f('B')} />
      <polygon points="1,41 41,41 31,29 11,29"  fill={f('L')} />
      <polygon points="1,1 1,41 11,29 11,13"    fill={f('M')} />
      <polygon points="41,1 41,41 31,29 31,13"  fill={f('D')} />
      <rect x="11" y="13" width="20" height="16" fill={f('O')} />
      <rect x="1" y="1" width="40" height="40" rx="3"
        stroke={border} strokeWidth={mainColor ? 1.8 : 1.2} fill="none" />
      <line x1="1"  y1="1"  x2="11" y2="13" stroke={line} strokeWidth="0.9"/>
      <line x1="41" y1="1"  x2="31" y2="13" stroke={line} strokeWidth="0.9"/>
      <line x1="1"  y1="41" x2="11" y2="29" stroke={line} strokeWidth="0.9"/>
      <line x1="41" y1="41" x2="31" y2="29" stroke={line} strokeWidth="0.9"/>
      <line x1="11" y1="13" x2="31" y2="13" stroke={line} strokeWidth="0.9"/>
      <line x1="11" y1="29" x2="31" y2="29" stroke={line} strokeWidth="0.9"/>
      <line x1="11" y1="13" x2="11" y2="29" stroke={line} strokeWidth="0.9"/>
      <line x1="31" y1="13" x2="31" y2="29" stroke={line} strokeWidth="0.9"/>
      {first?.condition && (
        <text x="21" y="22" textAnchor="middle" dominantBaseline="middle"
          fontSize="7" fontWeight="800" fill={mainColor}
          fontFamily="system-ui,sans-serif" paintOrder="stroke"
          stroke="#fff" strokeWidth="2">
          {first.condition}
        </text>
      )}
    </svg>
  );
}

// ── Tooth Condition Modal ─────────────────────────────────────────────────────
function ToothModal({ toothNum, conditions, onSave, onClose }) {
  const isAnterior = [11,12,13,21,22,23,31,32,33,41,42,43,51,52,53,61,62,63,71,72,73,81,82,83].includes(toothNum);
  const surfaceOpts = isAnterior
    ? ['Seluruh','Mesial','Distal','Labial','Lingual','Insisal']
    : SURFACE_OPTS;

  const [rows, setRows] = useState(
    conditions.length
      ? conditions.map((c) => ({ ...c }))
      : [{ surface: 'Seluruh', condition: '', diagnosis: '', icd10: '', notes: '' }]
  );

  function updateRow(i, field, val) {
    setRows((p) => p.map((r, idx) => idx === i ? { ...r, [field]: val } : r));
  }
  function addRow()     { setRows((p) => [...p, { surface: 'Seluruh', condition: '', diagnosis: '', icd10: '', notes: '' }]); }
  function removeRow(i) { setRows((p) => p.filter((_, idx) => idx !== i)); }

  const previewColor = rows[0]?.condition ? (CONDITION_COLOR[rows[0].condition] || '#0369a1') : '#9ca3af';

  return (
    <div className="modal-backdrop" style={{ zIndex: 1100 }} onClick={onClose}>
      <div className="tm-box" onClick={(e) => e.stopPropagation()}>
        <div className="tm-header">
          <div className="tm-header-left">
            <div className="tm-tooth-preview" style={{ borderColor: previewColor, background: previewColor + '15' }}>
              <ToothIcon conditions={rows.filter((r) => r.condition)} size={40} />
            </div>
            <div>
              <div className="tm-title">Kondisi Gigi</div>
              <div className="tm-subtitle">
                Gigi <strong>{toothNum}</strong>
                <span className="tm-type-badge">{isAnterior ? 'Anterior' : 'Posterior'}</span>
              </div>
            </div>
          </div>
          <button className="tm-close" onClick={onClose}><X size={18}/></button>
        </div>

        <div className="tm-body">
          {rows.map((row, i) => (
            <div key={i} className="tm-card">
              <div className="tm-card-head">
                <select className="tm-select tm-surface"
                  value={row.surface} onChange={(e) => updateRow(i, 'surface', e.target.value)}>
                  {surfaceOpts.map((s) => <option key={s}>{s}</option>)}
                </select>
                {rows.length > 1 && (
                  <button className="tm-remove" onClick={() => removeRow(i)}>
                    <Trash2 size={13}/> Hapus
                  </button>
                )}
              </div>
              <div className="tm-card-grid">
                <div className="tm-field">
                  <label>Keadaan Gigi <span className="tm-req">*</span></label>
                  <select className="tm-select"
                    value={row.condition} onChange={(e) => updateRow(i, 'condition', e.target.value)}>
                    {TOOTH_CONDITIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div className="tm-field">
                  <label>Diagnosis</label>
                  <input className="tm-input" value={row.diagnosis}
                    onChange={(e) => updateRow(i, 'diagnosis', e.target.value)}
                    placeholder="Masukkan diagnosis" />
                </div>
              </div>
              <div className="tm-field">
                <label>ICD-10</label>
                <input className="tm-input" value={row.icd10}
                  onChange={(e) => updateRow(i, 'icd10', e.target.value)}
                  placeholder="Kode atau nama penyakit" />
              </div>
              <div className="tm-field">
                <label>Catatan</label>
                <input className="tm-input" value={row.notes}
                  onChange={(e) => updateRow(i, 'notes', e.target.value)}
                  placeholder="Catatan tambahan" />
              </div>
            </div>
          ))}
          <button className="tm-add-surface" onClick={addRow}>
            <Plus size={14}/> + Permukaan &amp; Keadaan Gigi
          </button>
        </div>

        <div className="tm-footer">
          <button className="tm-btn-cancel" onClick={onClose}>Batal</button>
          {conditions.length > 0 && (
            <button className="tm-btn-clear" onClick={() => onSave([])}>Hapus Kondisi</button>
          )}
          <button className="tm-btn-save" onClick={() => onSave(rows.filter((r) => r.condition))}>Simpan</button>
        </div>
      </div>
    </div>
  );
}

// ── Odontogram ────────────────────────────────────────────────────────────────
function Odontogram({ value = {}, onChange, readOnly }) {
  const [selectedTooth, setSelectedTooth] = useState(null);
  const handleClick = useCallback((num) => { if (!readOnly) setSelectedTooth(num); }, [readOnly]);

  function saveTooth(conds) {
    const next = { ...value };
    if (conds.length === 0) delete next[selectedTooth];
    else next[selectedTooth] = conds;
    onChange(next);
    setSelectedTooth(null);
  }

  function Tooth({ num, size = 42, numPos = 'top' }) {
    const conds = value[num] || [];
    const color = conds[0] ? (CONDITION_COLOR[conds[0].condition] || '#0369a1') : null;
    const tip   = conds.length ? conds.map((c) => `${c.condition} (${c.surface})`).join(', ') : `Gigi ${num}`;
    return (
      <div className={`odo-cell${color ? ' odo-cell-filled' : ''}${readOnly ? '' : ' odo-cell-click'}`}
        onClick={() => handleClick(num)} title={tip}
        style={{ '--tc': color || '#c4c9d4' }}>
        {numPos === 'top' && <span className="odo-num">{num}</span>}
        <ToothIcon conditions={conds} size={size}/>
        {numPos === 'bottom' && <span className="odo-num">{num}</span>}
      </div>
    );
  }

  function Row({ teeth, size, numPos, pad = 0 }) {
    return (
      <div className="odo-row">
        {pad > 0 && <div style={{ width: `${pad * 44}px`, flexShrink: 0 }}/>}
        {teeth.map((n) => <Tooth key={n} num={n} size={size} numPos={numPos}/>)}
      </div>
    );
  }

  return (
    <div className="odontogram-wrap">
      <div className="odo-chart">
        {/* Upper */}
        <div className="odo-quadrant">
          <div className="odo-half-row">
            <Row teeth={[18,17,16,15,14,13,12,11]} size={42} numPos="top"/>
            <div className="odo-midgap"/>
            <Row teeth={[21,22,23,24,25,26,27,28]} size={42} numPos="top"/>
          </div>
          <div className="odo-half-row odo-deciduous-row">
            <div className="odo-deciduous-offset"/>
            <Row teeth={[55,54,53,52,51]} size={34} numPos="top"/>
            <div className="odo-midgap"/>
            <Row teeth={[61,62,63,64,65]} size={34} numPos="top"/>
          </div>
        </div>

        <div className="odo-midline"/>

        {/* Lower */}
        <div className="odo-quadrant">
          <div className="odo-half-row odo-deciduous-row">
            <div className="odo-deciduous-offset"/>
            <Row teeth={[85,84,83,82,81]} size={34} numPos="bottom"/>
            <div className="odo-midgap"/>
            <Row teeth={[71,72,73,74,75]} size={34} numPos="bottom"/>
          </div>
          <div className="odo-half-row">
            <Row teeth={[48,47,46,45,44,43,42,41]} size={42} numPos="bottom"/>
            <div className="odo-midgap"/>
            <Row teeth={[31,32,33,34,35,36,37,38]} size={42} numPos="bottom"/>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="odo-legend">
        {Object.entries(CONDITION_COLOR).map(([code, color]) => (
          <span key={code} className="odo-leg-item">
            <span className="odo-leg-dot" style={{ background: color }}/>
            {code}
          </span>
        ))}
      </div>

      {selectedTooth !== null && (
        <ToothModal
          toothNum={selectedTooth}
          conditions={value[selectedTooth] || []}
          onSave={saveTooth}
          onClose={() => setSelectedTooth(null)}
        />
      )}
    </div>
  );
}

function SelectField({ label, required, value, onChange, options, readOnly }) {
  return (
    <div className="dental-select-field">
      <label className="dental-select-label">{label}{required ? ' *' : ''}</label>
      <select
        className="dental-select"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={readOnly}
      >
        {options.map((o) => <option key={o}>{o}</option>)}
      </select>
    </div>
  );
}

// ── Main Modal ─────────────────────────────────────────────────────────────────
export function DentalFormModal({ data, onClose, onSaved, onGoToAction }) {
  const note   = data.dental_note || {};
  const visit  = data.visit;
  const client = data.client;
  const isExistingFinal = /^(FINAL|AMENDED)$/i.test(note.draft_status || '');
  const matchingStaff   = data.staff?.find((r) => r.id === note.nakes_id)
    || data.staff?.find((r) => r.name === (note.doctor_name || visit.doctor_name));
  const doctorName = matchingStaff?.name || visit.doctor_name || '';

  const [showPhoto,        setShowPhoto]        = useState(false);
  const [correctionMode,   setCorrectionMode]   = useState(!isExistingFinal);
  const [busy,             setBusy]             = useState('');
  const [error,            setError]            = useState('');
  const [savedMsg,         setSavedMsg]         = useState('');
  const [histTab,          setHistTab]          = useState('Riwayat');
  const [histOnlyMe,       setHistOnlyMe]       = useState(false);
  const [history,          setHistory]          = useState([]);
  const [showMoreKeluhan,  setShowMoreKeluhan]  = useState(false);
  const [showExamDetail,   setShowExamDetail]   = useState(false);

  const readOnly = isExistingFinal && !correctionMode;

  const [form, setForm] = useState({
    nakes_id:            matchingStaff?.id || '',
    chief_complaint:     nullable(note.chief_complaint),
    dental_history:      nullable(note.dental_history),
    allergies:           nullable(note.allergies),
    consciousness:       nullable(note.consciousness) || 'Compos Mentis',
    blood_pressure:      nullable(note.vital_signs?.blood_pressure),
    bpm:                 nullable(note.vital_signs?.bpm),
    temperature:         nullable(note.vital_signs?.temperature),
    intraoral_exam:      nullable(note.intraoral_exam),
    extraoral_exam:      nullable(note.extraoral_exam),
    diagnosis:           nullable(note.diagnosis),
    oklusi:              nullable(note.oklusi) || 'Normal Bite',
    torus_palatinus:     nullable(note.torus_palatinus) || 'Tidak ada',
    torus_mandibularis:  nullable(note.torus_mandibularis) || 'Tidak ada',
    palatum:             nullable(note.palatum) || 'Sedang',
    diastema:            nullable(note.diastema) || 'Tidak ada',
    gigi_anomali:        nullable(note.gigi_anomali),
    lain_lain:           nullable(note.lain_lain),
    icd10_primary:       note.icd10_primary  || null,
    icd10_secondary:     note.icd10_secondary || [],
    odontogram:          note.odontogram || {},
    planned_procedures:  nullable(note.treatment_plan?.planned_procedures),
    procedures:          normalizeProcedureList(note.treatment_plan?.procedures),
    medication_plan:     nullable(note.treatment_plan?.medication_plan),
    action_notes:        nullable(note.action_notes),
    education_notes:     nullable(note.education_notes),
    followup_plan:       nullable(note.followup_plan),
    followup_date:       nullable(note.followup_date),
  });

  function update(name, value) { setForm((c) => ({ ...c, [name]: value })); }
  function addProcedure(serviceId) {
    if (!serviceId || form.procedures.some((row) => row.service_id === serviceId)) return;
    const service = dentalServices.find((row) => row.id === serviceId);
    if (!service) return;
    update('procedures', [
      ...form.procedures,
      {
        service_id: service.id,
        name: service.name,
        price: service.price ?? 0,
        duration: service.duration ?? 0,
      },
    ]);
  }
  function removeProcedure(serviceId) {
    update('procedures', form.procedures.filter((row) => row.service_id !== serviceId));
  }

  // Load visit history
  useEffect(() => {
    if (!client?.id) return;
    loadClientVisitHistory(client.id).then(setHistory).catch(() => {});
  }, [client?.id]);
  const [dentalServices, setDentalServices] = useState([]);
  useEffect(() => {
    loadDentalServices().then(setDentalServices).catch(() => {});
  }, []);

  const visibleHistory = histOnlyMe
    ? history.filter((h) => h.nakes_id === form.nakes_id || h.doctor_name === doctorName)
    : history;

  const odontogramCount = Object.keys(form.odontogram).length;

  function buildPayload() {
    return {
      nakes_id:           form.nakes_id,
      chief_complaint:    form.chief_complaint,
      dental_history:     form.dental_history,
      allergies:          form.allergies,
      consciousness:      form.consciousness,
      vital_signs: {
        blood_pressure: form.blood_pressure,
        bpm:            form.bpm,
        temperature:    form.temperature,
      },
      intraoral_exam:     form.intraoral_exam,
      extraoral_exam:     form.extraoral_exam,
      diagnosis:          form.diagnosis,
      oklusi:             form.oklusi,
      torus_palatinus:    form.torus_palatinus,
      torus_mandibularis: form.torus_mandibularis,
      palatum:            form.palatum,
      diastema:           form.diastema,
      gigi_anomali:       form.gigi_anomali,
      lain_lain:          form.lain_lain,
      icd10_primary:      form.icd10_primary,
      icd10_secondary:    form.icd10_secondary,
      odontogram:         form.odontogram,
      treatment_plan: {
        planned_procedures: form.planned_procedures,
        procedures: form.procedures,
        medication_plan:    form.medication_plan,
      },
      action_notes:       form.action_notes,
      education_notes:    form.education_notes,
      followup_plan:      form.followup_plan,
      followup_date:      form.followup_date,
    };
  }

  function validate(finalize) {
    if (finalize && !form.nakes_id)               return 'Dokter gigi / nakes pemeriksa wajib dipilih.';
    if (finalize && !form.chief_complaint.trim())  return 'Keluhan utama wajib diisi.';
    if (finalize && !form.diagnosis.trim())        return 'Diagnosis wajib diisi.';
    if (finalize && !form.procedures.length)       return 'Pilih minimal satu perawatan dari Master Tindakan sebelum final.';
    if (form.blood_pressure.trim() && !/^\d{2,3}\s*\/\s*\d{2,3}$/.test(form.blood_pressure.trim()))
      return 'Format tekanan darah: 120/80';
    return '';
  }

  async function submit(finalize) {
    const err = validate(finalize);
    setError(err);
    if (err) return;
    if (finalize && !window.confirm(
      isExistingFinal ? 'Finalisasi koreksi pemeriksaan dental ini?' : 'Finalisasi pemeriksaan dental ini?'
    )) return;

    setBusy(finalize ? 'final' : 'draft');
    try {
      const result = await saveDentalNote(visit.id, buildPayload(), finalize);
      if (finalize) {
        onSaved(result, isExistingFinal ? 'Koreksi berhasil difinalkan.' : 'Pemeriksaan dental difinalkan.');
      } else {
        setSavedMsg('✓ Draft berhasil disimpan — ' + new Date().toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' }));
        setTimeout(() => setSavedMsg(''), 4000);
      }
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy('');
    }
  }

  const inp = { padding: '7px 10px', border: '1px solid #c9cfd8', borderRadius: 5, fontSize: 13, background: '#fff', width: '100%', boxSizing: 'border-box' };
  const ta  = { ...inp, resize: 'vertical', fontFamily: 'inherit' };

  return (
    <div className="emr-backdrop" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="emr-shell" role="dialog" aria-modal="true">

        {/* ── Top bar ── */}
        <div className="emr-topbar">
          <div className="emr-topbar-info">
            <span><b>PASIEN:</b> {client.name}</span>
            <span className="emr-sep">·</span>
            <span><b>UMUR:</b> {ageFromBirthDate(client.birth_date)}</span>
            <span className="emr-sep">·</span>
            <span><b>JK:</b> {client.gender === 'Laki-laki' ? 'Pria' : client.gender === 'Perempuan' ? 'Wanita' : client.gender || '—'}</span>
            <span className="emr-sep">·</span>
            <span><b>🦷 POLI GIGI</b></span>
            {note.draft_status && (
              <span className={`emr-status-badge emr-status-${(note.draft_status||'').toLowerCase()}`}>
                {note.draft_status}
              </span>
            )}
          </div>
          {savedMsg && <span className="emr-saved-msg">{savedMsg}</span>}
          <div className="emr-topbar-actions">
            {readOnly ? (
              <button className="emr-btn emr-btn-primary" onClick={onGoToAction} disabled={!!busy}>
                <Activity size={15}/> Tindakan
              </button>
            ) : (
              <>
                <button className="emr-btn emr-btn-soft" onClick={() => submit(false)} disabled={!!busy}>
                  <Save size={15}/> {busy === 'draft' ? 'Menyimpan…' : 'Simpan'}
                </button>
                <button className="emr-btn emr-btn-success" onClick={() => submit(true)} disabled={!!busy}>
                  <ShieldCheck size={15}/> {busy === 'final' ? 'Memfinalkan…' : (isExistingFinal ? 'Finalisasi Koreksi' : 'FINAL')}
                </button>
              </>
            )}
            <button className="emr-btn emr-btn-close" onClick={onClose} disabled={!!busy}>
              <X size={15}/> Tutup
            </button>
          </div>
        </div>

        {/* ── Meta bar ── */}
        <div className="emr-metabar">
          <div className="emr-meta-item">
            <span className="emr-meta-lbl">TANGGAL</span>
            <span className="emr-meta-val">{dateLabel(visit.visit_date)}</span>
          </div>
          <div className="emr-meta-item">
            <span className="emr-meta-lbl">NO. KUNJUNGAN</span>
            <span className="emr-meta-val">{visit.visit_number || '—'}</span>
          </div>
          <div className="emr-meta-item emr-meta-doctor">
            <span className="emr-meta-lbl">DOKTER GIGI / NAKES</span>
            <select className="emr-doctor-select" value={form.nakes_id}
              onChange={(e) => update('nakes_id', e.target.value)}
              disabled={readOnly || !!busy}
            >
              <option value="">— Pilih —</option>
              {(data.staff || []).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
          <div className="emr-meta-item">
            <span className="emr-meta-lbl">POLIKLINIK</span>
            <span className="emr-meta-val">🦷 Poli Gigi</span>
          </div>
          <div className="emr-meta-item">
            <span className="emr-meta-lbl">RUANGAN</span>
            <span className="emr-meta-val">{visit.room_name || 'Tidak ditentukan'}</span>
          </div>
          <div className="emr-meta-item">
            <span className="emr-meta-lbl">ALERGI</span>
            <span className="emr-meta-val emr-meta-alergi">{form.allergies || 'Tidak ada'}</span>
          </div>
          <div className="emr-meta-item">
            <span className="emr-meta-lbl">ODONTOGRAM</span>
            <span className="emr-meta-val">{odontogramCount > 0 ? `${odontogramCount} gigi tercatat` : 'Belum diisi'}</span>
          </div>
        </div>

        {/* ── Alerts ── */}
        {(error || isExistingFinal) && (
          <div className="emr-alerts">
            {error && <div className="alert alert-danger">{error}</div>}
            {isExistingFinal && !correctionMode && (
              <div className="alert alert-info clinical-lock-notice" style={{ margin: 0 }}>
                <LockKeyhole size={16}/>
                <span>Rekam dental sudah {note.draft_status}. Data baca-saja.</span>
                {data.can_correct_final && (
                  <button className="button button-mini button-quiet" onClick={() => setCorrectionMode(true)}>Mulai koreksi</button>
                )}
              </div>
            )}
            {isExistingFinal && correctionMode && (
              <div className="alert alert-warning clinical-lock-notice" style={{ margin: 0 }}>
                <ShieldCheck size={16}/>
                <span>Mode koreksi aktif. Versi sebelumnya tersimpan di audit.</span>
              </div>
            )}
          </div>
        )}

        {/* ── Body: split panel ── */}
        <div className="emr-body">

          {/* ────── LEFT: form ────── */}
          <div className="emr-form-panel" role="main">
            <fieldset disabled={readOnly || !!busy} style={{ border: 'none', padding: 0, margin: 0 }}>

              {/* KELUHAN UTAMA */}
              <div className="emr-section">
                <div className="emr-section-head">
                  <span className="emr-section-label">KELUHAN UTAMA</span>
                  <span className="emr-badge emr-badge-blue">SUBJECTIVE</span>
                </div>
                <textarea style={ta} rows={3} value={form.chief_complaint}
                  onChange={(e) => update('chief_complaint', e.target.value)}
                  placeholder="Keluhan yang membawa pasien ke klinik, onset, durasi, karakteristik…"
                  maxLength={5000}
                />
                <button type="button" className="emr-expand-btn" onClick={() => setShowMoreKeluhan(!showMoreKeluhan)}>
                  <ChevronDown size={14} className={showMoreKeluhan ? 'emr-chevron-open' : ''}/> Anamnesa lengkap
                </button>
                {showMoreKeluhan && (
                  <div className="emr-extra-fields">
                    <label className="emr-extra-label">Riwayat dental</label>
                    <textarea style={ta} rows={3} value={form.dental_history}
                      onChange={(e) => update('dental_history', e.target.value)}
                      placeholder="Riwayat tambal/cabut, trauma, behel, scaling terakhir…"
                      maxLength={3000}/>
                    <label className="emr-extra-label">Alergi / perhatian khusus</label>
                    <textarea style={ta} rows={2} value={form.allergies}
                      onChange={(e) => update('allergies', e.target.value)}
                      placeholder="Alergi obat/anestesi, kehamilan, hipertensi, diabetes, obat pengencer darah…"
                      maxLength={2000}/>
                  </div>
                )}
              </div>

              {/* TANDA VITAL */}
              <div className="emr-section">
                <div className="emr-section-head">
                  <span className="emr-section-label">TANDA VITAL</span>
                  <span className="emr-badge emr-badge-teal">OBJECTIVE</span>
                </div>
                <div className="emr-vitals-inline">
                  <div className="emr-vital-pair">
                    <label>Kesadaran</label>
                    <select style={{ ...inp, width: 160 }} value={form.consciousness}
                      onChange={(e) => update('consciousness', e.target.value)}>
                      {['Compos Mentis','Somnolence','Sopor','Coma'].map((o) => <option key={o}>{o}</option>)}
                    </select>
                  </div>
                  <div className="emr-vital-pair">
                    <label>TD</label>
                    <input style={{ ...inp, width: 90 }} value={form.blood_pressure}
                      onChange={(e) => update('blood_pressure', e.target.value)} placeholder="120/80" maxLength={9}/>
                    <span>mmHg</span>
                  </div>
                  <div className="emr-vital-pair">
                    <label>Nadi</label>
                    <input style={{ ...inp, width: 65 }} type="number" min="20" max="300" value={form.bpm}
                      onChange={(e) => update('bpm', e.target.value)} placeholder="80"/>
                    <span>/mnt</span>
                  </div>
                  <div className="emr-vital-pair">
                    <label>Suhu</label>
                    <input style={{ ...inp, width: 70 }} type="number" step="0.1" min="30" max="45" value={form.temperature}
                      onChange={(e) => update('temperature', e.target.value)} placeholder="36.5"/>
                    <span>°C</span>
                  </div>
                </div>
              </div>

              {/* PEMERIKSAAN FISIK */}
              <div className="emr-section">
                <div className="emr-section-head">
                  <span className="emr-section-label">PEMERIKSAAN FISIK</span>
                </div>
                <textarea style={ta} rows={2} value={form.extraoral_exam}
                  onChange={(e) => update('extraoral_exam', e.target.value)}
                  placeholder="Ekstraoral: wajah, kelenjar limfe, TMJ, otot mastikasi, asimetri…"
                  maxLength={3000}/>
                <button type="button" className="emr-expand-btn" onClick={() => setShowExamDetail(!showExamDetail)}>
                  <ChevronDown size={14} className={showExamDetail ? 'emr-chevron-open' : ''}/> Detail intraoral
                </button>
                {showExamDetail && (
                  <div className="emr-extra-fields">
                    <label className="emr-extra-label">Pemeriksaan Intraoral</label>
                    <textarea style={ta} rows={4} value={form.intraoral_exam}
                      onChange={(e) => update('intraoral_exam', e.target.value)}
                      placeholder="Mukosa, gingiva, karang gigi, karies, nyeri tekan, mobilitas, abses…"
                      maxLength={5000}/>
                  </div>
                )}
              </div>

              {/* DIAGNOSA */}
              <div className="emr-section">
                <div className="emr-section-head">
                  <span className="emr-section-label">DIAGNOSA</span>
                  <span className="emr-badge emr-badge-blue">ICD-10</span>
                  <span className="emr-badge emr-badge-orange">ASSESSMENT</span>
                </div>
                <textarea style={ta} rows={3} value={form.diagnosis}
                  onChange={(e) => update('diagnosis', e.target.value)}
                  placeholder="Diagnosis utama berdasarkan anamnesa dan pemeriksaan klinis…"
                  maxLength={5000}/>
                <ICD10Picker value={form.icd10_primary} onChange={(v) => update('icd10_primary', v)}
                  readOnly={readOnly} placeholder="Cari kode ICD-10 diagnosis utama…"/>
                {(form.icd10_secondary?.length > 0 || !readOnly) && (
                  <ICD10Picker value={form.icd10_secondary} onChange={(v) => update('icd10_secondary', v)}
                    multiple readOnly={readOnly} placeholder="Tambah diagnosis sekunder…"/>
                )}

                {/* Jaringan keras & lunak */}
                <div style={{ marginTop: 10 }}>
                  <div className="emr-dental-sub-label">PEMERIKSAAN JARINGAN KERAS &amp; LUNAK</div>
                  <div className="emr-dental-assess-grid">
                    <SelectField label="Oklusi"            value={form.oklusi}             onChange={(v) => update('oklusi', v)}             options={OKLUSI_OPTS}   readOnly={readOnly}/>
                    <SelectField label="Torus Palatinus"   value={form.torus_palatinus}    onChange={(v) => update('torus_palatinus', v)}    options={TORUS_OPTS}    readOnly={readOnly}/>
                    <SelectField label="Torus Mandibularis" value={form.torus_mandibularis} onChange={(v) => update('torus_mandibularis', v)} options={TORUS_OPTS}    readOnly={readOnly}/>
                    <SelectField label="Palatum"           value={form.palatum}             onChange={(v) => update('palatum', v)}             options={PALATUM_OPTS}  readOnly={readOnly}/>
                    <SelectField label="Diastema"          value={form.diastema}            onChange={(v) => update('diastema', v)}            options={DIASTEMA_OPTS} readOnly={readOnly}/>
                    <div className="dental-select-field">
                      <label className="dental-select-label">Gigi Anomali</label>
                      <input style={inp} value={form.gigi_anomali}
                        onChange={(e) => update('gigi_anomali', e.target.value)}
                        placeholder="Gigi Anomali" disabled={readOnly}/>
                    </div>
                  </div>
                  <div className="dental-select-field" style={{ marginTop: 8 }}>
                    <label className="dental-select-label">Lain – lain</label>
                    <input style={inp} value={form.lain_lain}
                      onChange={(e) => update('lain_lain', e.target.value)}
                      placeholder="Keterangan tambahan" disabled={readOnly}/>
                  </div>
                </div>
              </div>

              {/* ODONTOGRAM */}
              <div className="emr-section">
                <div className="emr-section-head">
                  <span className="emr-section-label">ODONTOGRAM GIGI</span>
                  {odontogramCount > 0 && (
                    <span className="emr-badge emr-badge-teal">{odontogramCount} gigi</span>
                  )}
                  <small className="emr-hint-inline">klik gigi untuk mengisi kondisi</small>
                </div>
                <Odontogram
                  value={form.odontogram}
                  onChange={(v) => update('odontogram', v)}
                  readOnly={readOnly}
                />
              </div>

              {/* PERENCANAAN */}
              <div className="emr-section">
                <div className="emr-section-head">
                  <span className="emr-section-label">PERENCANAAN</span>
                  <span className="emr-badge emr-badge-teal">PLAN</span>
                </div>
                <div className="emr-plan-subsection">
                  <div className="emr-plan-sub-label">PERAWATAN</div>
                  <select
                    style={inp}
                    value=""
                    onChange={(e) => addProcedure(e.target.value)}
                    disabled={readOnly}
                  >
                    <option value="">-- Pilih perawatan dari Master Tindakan --</option>
                    {dentalServices
                      .filter((service) => !form.procedures.some((row) => row.service_id === service.id))
                      .map((service) => (
                        <option key={service.id} value={service.id}>
                          {service.name}{service.duration ? ` - ${service.duration} menit` : ''}
                        </option>
                      ))}
                  </select>
                  {form.procedures.length ? (
                    <div className="dental-procedure-list">
                      {form.procedures.map((procedure) => (
                        <div key={procedure.service_id || procedure.name} className="dental-procedure-chip">
                          <span>{procedure.name}</span>
                          {!readOnly && (
                            <button type="button" onClick={() => removeProcedure(procedure.service_id)} aria-label={`Hapus ${procedure.name}`}>
                              <X size={13}/>
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="emr-placeholder-hint">Belum ada perawatan dipilih.</p>
                  )}
                  <textarea style={ta} rows={2} value={form.planned_procedures}
                    onChange={(e) => update('planned_procedures', e.target.value)}
                    placeholder="Catatan tambahan tindakan, rujuk rontgen, kontrol..."
                    maxLength={5000}/>
                </div>
                <div className="emr-plan-subsection">
                  <div className="emr-plan-sub-label">RENCANA OBAT / RESEP</div>
                  <textarea style={ta} rows={2} value={form.medication_plan}
                    onChange={(e) => update('medication_plan', e.target.value)}
                    placeholder="Analgesik, antibiotik bila indikasi, obat kumur, antiinflamasi..."
                    maxLength={3000}/>
                </div>
                <div className="emr-plan-subsection">
                  <div className="emr-plan-sub-label">CATATAN TINDAKAN</div>
                  <textarea style={ta} rows={2} value={form.action_notes}
                    onChange={(e) => update('action_notes', e.target.value)}
                    placeholder="Catatan tindakan yang sudah dilakukan pada kunjungan ini…"
                    maxLength={5000}/>
                </div>
                <div className="emr-plan-subsection">
                  <div className="emr-plan-sub-label">EDUKASI PASIEN</div>
                  <textarea style={ta} rows={2} value={form.education_notes}
                    onChange={(e) => update('education_notes', e.target.value)}
                    placeholder="Instruksi kebersihan mulut, pantangan makanan, tanda bahaya…"
                    maxLength={5000}/>
                </div>
              </div>

              {/* JADWAL KONTROL */}
              <div className="emr-section">
                <div className="emr-section-head">
                  <span className="emr-section-label" style={{ color: '#0891b2' }}>Jadwal Kontrol</span>
                </div>
                <div className="emr-followup-row">
                  <input style={{ ...inp, width: 170 }} type="date" value={form.followup_date}
                    onChange={(e) => update('followup_date', e.target.value)}/>
                  <input style={{ ...inp, flex: 1 }} value={form.followup_plan}
                    onChange={(e) => update('followup_plan', e.target.value)}
                    placeholder="Kontrol 7 hari / setelah nyeri reda…" maxLength={500}/>
                </div>
              </div>

              {/* FOTO BEFORE / AFTER */}
              <div className="emr-section emr-section-last">
                <button
                  type="button"
                  className="emr-dental-photo-btn"
                  onClick={() => setShowPhoto(true)}
                >
                  <Camera size={15}/> Foto Before / After
                </button>
              </div>

            </fieldset>
          </div>

          {/* ────── RIGHT: history ────── */}
          <div className="emr-history-panel">
            <div className="emr-hist-tabs">
              {HIST_TABS.map((t) => (
                <button key={t} type="button"
                  className={`emr-hist-tab ${histTab === t ? 'emr-hist-tab-active' : ''}`}
                  onClick={() => setHistTab(t)}>
                  {t}
                </button>
              ))}
            </div>

            <div className="emr-hist-body">
              {histTab === 'Riwayat' && (
                <>
                  <div className="emr-hist-title">
                    <span>RIWAYAT KUNJUNGAN</span>
                    <button type="button" className="emr-hist-print" title="Cetak riwayat">
                      <Printer size={14}/> RIWAYAT
                    </button>
                  </div>
                  {doctorName && (
                    <label className="emr-hist-filter">
                      <input type="checkbox" checked={histOnlyMe} onChange={(e) => setHistOnlyMe(e.target.checked)}/>
                      Hanya {doctorName}
                    </label>
                  )}
                  <div className="emr-hist-table-wrap">
                    <table className="emr-hist-table">
                      <thead>
                        <tr>
                          <th>TGL</th>
                          <th>KELUHAN</th>
                          <th>DIAGNOSA</th>
                          <th>PERAWATAN</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleHistory.length === 0 && (
                          <tr><td colSpan={5} className="emr-hist-empty">Belum ada riwayat kunjungan.</td></tr>
                        )}
                        {visibleHistory.map((h) => {
                          const isCurrent = h.id === visit.id;
                          return (
                            <tr key={h.id} className={isCurrent ? 'emr-hist-row-current' : ''}>
                              <td className="emr-hist-date" style={{ whiteSpace: 'nowrap' }}>
                                {dateLabel(h.visit_date)}
                              </td>
                              <td className="emr-hist-cell">
                                {h.keluhan
                                  ? <span title={h.keluhan}>{h.keluhan.slice(0, 50)}{h.keluhan.length > 50 ? '…' : ''}</span>
                                  : <span className="emr-hist-empty-cell">—</span>}
                              </td>
                              <td className="emr-hist-cell">
                                {h.icd10?.code
                                  ? <code className="emr-hist-icd">{h.icd10.code}</code>
                                  : null}
                                {h.diagnosa
                                  ? <span title={h.diagnosa}>{h.diagnosa.slice(0, 40)}{h.diagnosa.length > 40 ? '…' : ''}</span>
                                  : <span className="emr-hist-empty-cell">—</span>}
                              </td>
                              <td className="emr-hist-cell">
                                {h.tindakan.length > 0
                                  ? h.tindakan.map((t, i) => <div key={i} className="emr-hist-tindakan">› {t}</div>)
                                  : h.catatan
                                    ? <span title={h.catatan}>{h.catatan.slice(0, 60)}…</span>
                                    : <span className="emr-hist-empty-cell">—</span>}
                              </td>
                              <td className="emr-hist-actions">
                                <button type="button" className="emr-hist-view" title="Lihat detail">
                                  <Eye size={13}/>
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {histTab === 'Vital' && (
                <div className="emr-hist-stub">
                  <div className="emr-hist-title"><span>TANDA-TANDA VITAL</span></div>
                  <table className="emr-hist-table">
                    <thead><tr><th>TGL</th><th>TD</th><th>DN</th><th>Suhu</th></tr></thead>
                    <tbody>
                      {history.filter((h) => h.vital_signs).length === 0 && (
                        <tr><td colSpan={4} className="emr-hist-empty">Belum ada data vital.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {histTab === 'Alergi' && (
                <div className="emr-hist-stub">
                  <div className="emr-hist-title"><span>ALERGI</span></div>
                  {form.allergies
                    ? <p className="emr-hist-alergi-text">{form.allergies}</p>
                    : <p className="emr-hist-empty">Tidak ada catatan alergi.</p>}
                </div>
              )}

              {!['Riwayat','Vital','Alergi'].includes(histTab) && (
                <div className="emr-hist-stub">
                  <p className="emr-hist-empty">Tab {histTab} belum tersedia.</p>
                </div>
              )}
            </div>
          </div>

        </div>{/* emr-body */}
      </div>{/* emr-shell */}

      {/* Before/After Photo Modal */}
      {showPhoto && (
        <PhotoModal visitId={visit.id} context="dental" onClose={() => setShowPhoto(false)} />
      )}

      <style>{`
        /* ── EMR Shell ─────────────────────────────────────── */
        .emr-backdrop {
          position: fixed; inset: 0; z-index: 1000;
          display: flex; align-items: stretch; justify-content: stretch;
          background: rgba(0,0,0,.45);
        }
        .emr-shell {
          display: flex; flex-direction: column;
          width: 100%; height: 100%;
          background: #f0f4f8;
          overflow: hidden;
          font-size: 13px;
        }

        /* ── Top bar ───────────────────────────────────────── */
        .emr-topbar {
          display: flex; align-items: center; justify-content: space-between;
          background: #1e3a5f; color: #fff;
          padding: 10px 20px; gap: 16px; flex-shrink: 0;
          font-size: 15px;
        }
        .emr-topbar-info { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
        .emr-topbar-info b { font-weight: 700; color: #93c5fd; margin-right: 3px; }
        .emr-sep { color: #475569; font-size: 18px; }
        .emr-status-badge {
          padding: 3px 12px; border-radius: 5px; font-size: 13px; font-weight: 700;
          background: #22c55e; color: #fff; margin-left: 8px;
        }
        .emr-status-badge.emr-status-final { background: #22c55e; }
        .emr-status-badge.emr-status-draft { background: #f59e0b; }
        .emr-saved-msg {
          font-size: 12px; font-weight: 600; color: #86efac;
          background: rgba(22,163,74,.25); border: 1px solid rgba(22,163,74,.4);
          padding: 5px 12px; border-radius: 6px; white-space: nowrap;
        }
        .emr-topbar-actions { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
        .emr-btn {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 8px 16px; border-radius: 6px; border: none;
          font-size: 13px; font-weight: 700; cursor: pointer; transition: opacity .15s;
        }
        .emr-btn:disabled { opacity: .55; cursor: not-allowed; }
        .emr-btn-soft    { background: #e2e8f0; color: #1e3a5f; }
        .emr-btn-primary { background: #0369a1; color: #fff; }
        .emr-btn-success { background: #16a34a; color: #fff; }
        .emr-btn-close   { background: #dc2626; color: #fff; }
        .emr-btn-followup { background: #0891b2; color: #fff; }

        /* ── Meta bar ──────────────────────────────────────── */
        .emr-metabar {
          display: flex; align-items: stretch; gap: 6px;
          background: #2d4263;
          padding: 8px 12px;
          flex-shrink: 0; overflow-x: auto;
          border-bottom: 2px solid #1e3a5f;
        }
        .emr-meta-item {
          display: flex; flex-direction: column; gap: 4px;
          padding: 8px 14px;
          background: rgba(255,255,255,0.09);
          border: 1px solid rgba(255,255,255,0.15);
          border-radius: 7px;
          min-width: 0; flex-shrink: 0;
        }
        .emr-meta-lbl {
          font-size: 10px; font-weight: 700; color: #94a3b8;
          letter-spacing: .07em; text-transform: uppercase;
        }
        .emr-meta-val {
          font-size: 13px; font-weight: 600; color: #e2e8f0;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 180px;
        }
        .emr-meta-alergi { color: #fca5a5; }
        .emr-meta-doctor { min-width: 160px; }
        .emr-doctor-select {
          font-size: 13px; color: #e2e8f0; background: rgba(255,255,255,0.1);
          border: 1px solid rgba(255,255,255,0.2); border-radius: 5px; padding: 3px 6px;
          max-width: 200px;
        }
        .emr-doctor-select option { color: #1e293b; background: #fff; }

        /* ── Alerts ────────────────────────────────────────── */
        .emr-alerts { padding: 6px 12px; background: #f8fafc; flex-shrink: 0; display: flex; flex-direction: column; gap: 4px; }

        /* ── Body split ────────────────────────────────────── */
        .emr-body { display: flex; flex: 1; overflow: hidden; }

        /* ── Left panel ────────────────────────────────────── */
        .emr-form-panel {
          flex: 1; overflow-y: auto; background: #f8fafc;
          padding: 0; display: flex; flex-direction: column;
        }
        .emr-section {
          border-bottom: 1px solid #e2e8f0;
          padding: 12px 16px; background: #fff;
          display: flex; flex-direction: column; gap: 8px;
        }
        .emr-section-last { border-bottom: none; }
        .emr-section-head {
          display: flex; align-items: center; gap: 8px; margin-bottom: 2px;
        }
        .emr-section-label {
          font-size: 11px; font-weight: 800; color: #0369a1;
          text-transform: uppercase; letter-spacing: .06em;
        }
        .emr-badge {
          font-size: 9px; font-weight: 700; padding: 2px 7px; border-radius: 3px;
          text-transform: uppercase; letter-spacing: .04em;
        }
        .emr-badge-blue   { background: #dbeafe; color: #1d4ed8; }
        .emr-badge-teal   { background: #ccfbf1; color: #0f766e; }
        .emr-badge-orange { background: #ffedd5; color: #c2410c; }
        .emr-hint-inline { font-size: 11px; color: #9ca3af; }

        /* Vitals inline */
        .emr-vitals-inline {
          display: flex; align-items: center; flex-wrap: wrap; gap: 10px;
          padding: 8px 10px; background: #f0f9ff; border-radius: 6px;
          border: 1px solid #bae6fd;
        }
        .emr-vital-pair { display: flex; align-items: center; gap: 4px; }
        .emr-vital-pair label { font-size: 11px; font-weight: 700; color: #0369a1; white-space: nowrap; }
        .emr-vital-pair span { font-size: 11px; color: #6b7280; }

        /* Expand button */
        .emr-expand-btn {
          display: inline-flex; align-items: center; gap: 4px;
          font-size: 11px; color: #0369a1; font-weight: 600;
          background: none; border: none; cursor: pointer; padding: 2px 0;
          text-decoration: underline; text-underline-offset: 2px;
        }
        .emr-chevron-open { transform: rotate(180deg); }
        .emr-extra-fields { display: flex; flex-direction: column; gap: 6px; padding-top: 6px; }
        .emr-extra-label { font-size: 11px; font-weight: 600; color: #374151; }

        /* Plan subsections */
        .emr-plan-subsection { display: flex; flex-direction: column; gap: 4px; }
        .emr-plan-sub-label { font-size: 10px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: .05em; }

        /* Follow-up row */
        .emr-followup-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }

        /* ── Right panel ───────────────────────────────────── */
        .emr-history-panel {
          width: 400px; flex-shrink: 0; display: flex; flex-direction: column;
          border-left: 2px solid #e2e8f0; background: #fffde7; overflow: hidden;
        }
        .emr-hist-tabs {
          display: flex; flex-wrap: wrap; background: #fff; border-bottom: 1px solid #e2e8f0; flex-shrink: 0;
        }
        .emr-hist-tab {
          padding: 7px 12px; font-size: 12px; font-weight: 500; border: none;
          background: none; cursor: pointer; color: #475569; border-bottom: 2px solid transparent;
          white-space: nowrap;
        }
        .emr-hist-tab:hover { background: #f8fafc; }
        .emr-hist-tab-active { color: #0369a1; border-bottom-color: #0369a1; font-weight: 700; }
        .emr-hist-body { flex: 1; overflow-y: auto; padding: 0; }
        .emr-hist-title {
          display: flex; align-items: center; justify-content: space-between;
          padding: 10px 12px; background: #fff; border-bottom: 1px solid #e8e2d5;
          font-size: 12px; font-weight: 700; color: #1e3a5f;
        }
        .emr-hist-print {
          display: inline-flex; align-items: center; gap: 4px;
          font-size: 11px; font-weight: 600; color: #475569;
          background: #f1f5f9; border: 1px solid #cbd5e1;
          padding: 3px 9px; border-radius: 4px; cursor: pointer;
        }
        .emr-hist-filter {
          display: flex; align-items: center; gap: 6px;
          padding: 6px 12px; font-size: 12px; color: #374151;
          border-bottom: 1px solid #e8e2d5; background: #fafaf8;
          cursor: pointer; user-select: none;
        }
        .emr-hist-table-wrap { overflow-x: auto; }
        .emr-hist-table { width: 100%; border-collapse: collapse; font-size: 12px; }
        .emr-hist-table thead th {
          background: #334155; color: #cbd5e1; font-size: 10px; font-weight: 700;
          text-transform: uppercase; letter-spacing: .05em;
          padding: 6px 8px; text-align: left; white-space: nowrap;
        }
        .emr-hist-table tbody tr { border-bottom: 1px solid #e2e8f0; }
        .emr-hist-table tbody tr:hover { background: #fef9c3; }
        .emr-hist-row-current { background: #fef3c7 !important; border-left: 3px solid #f59e0b; }
        .emr-hist-date { padding: 6px 8px; color: #374151; font-weight: 500; font-size: 11px; }
        .emr-hist-cell { padding: 6px 8px; color: #374151; vertical-align: top; }
        .emr-hist-empty { text-align: center; color: #9ca3af; padding: 24px; font-style: italic; }
        .emr-hist-empty-cell { color: #9ca3af; }
        .emr-hist-icd { font-size: 10px; background: #dbeafe; color: #1d4ed8; padding: 1px 5px; border-radius: 3px; display: block; margin-bottom: 2px; }
        .emr-hist-tindakan { font-size: 11px; color: #0369a1; }
        .emr-hist-actions { padding: 4px 6px; }
        .emr-hist-view { background: none; border: 1px solid #e2e8f0; border-radius: 4px; padding: 3px 5px; cursor: pointer; color: #64748b; }
        .emr-hist-view:hover { background: #f0f9ff; color: #0369a1; }
        .emr-hist-stub { padding: 12px; }
        .emr-hist-alergi-text { padding: 10px 12px; background: #fee2e2; border-radius: 6px; color: #991b1b; font-size: 13px; }

        /* ── Dental-specific additions ──────────────────────── */
        .emr-dental-sub-label {
          font-size: 10px; font-weight: 700; letter-spacing: .07em;
          color: #94a3b8; text-transform: uppercase;
          margin: 8px 0 6px; display: flex; align-items: center; gap: 8px;
        }
        .emr-dental-assess-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
        }
        .emr-dental-photo-btn {
          display: inline-flex; align-items: center; gap: 7px;
          padding: 9px 18px;
          border: 1.5px dashed #7c3aed;
          border-radius: 8px;
          background: #faf5ff;
          color: #7c3aed;
          font-size: 13px; font-weight: 600;
          cursor: pointer;
        }
        .emr-dental-photo-btn:hover { background: #ede9fe; }
        .dental-procedure-list {
          display: flex; flex-wrap: wrap; gap: 6px;
          padding: 8px; border: 1px solid #dbeafe;
          border-radius: 8px; background: #f8fafc;
        }
        .dental-procedure-chip {
          display: inline-flex; align-items: center; gap: 7px;
          padding: 5px 8px; border-radius: 999px;
          background: #e0f2fe; color: #075985;
          font-size: 12px; font-weight: 700;
        }
        .dental-procedure-chip button {
          display: inline-flex; align-items: center; justify-content: center;
          width: 20px; height: 20px; border: 0; border-radius: 999px;
          background: rgba(7,89,133,.12); color: #075985;
          padding: 0; cursor: pointer;
        }
        .dental-procedure-chip button:hover { background: #bae6fd; }

        /* dental-select-field reused from old form */
        .dental-select-field { display: flex; flex-direction: column; gap: 4px; }
        .dental-select-label { font-size: 12px; font-weight: 600; color: #374151; }
        .dental-select {
          padding: 8px 10px;
          border: 1px solid #d1d5db; border-radius: 8px;
          font-size: 13px; background: #fff;
        }
        .dental-select:focus { outline: none; border-color: #16a34a; }

        /* ── ICD picker ── */
        .icd-picker { display:flex; flex-direction:column; gap:6px; }
        .icd-tags { display:flex; flex-wrap:wrap; gap:5px; }
        .icd-tag { display:inline-flex; align-items:center; gap:5px; background:#dbeafe; color:#1d4ed8; padding:3px 8px; border-radius:5px; font-size:12px; }
        .icd-tag code { font-size:11px; font-weight:700; }
        .icd-tag-remove { background:none; border:none; color:#1d4ed8; cursor:pointer; font-size:14px; line-height:1; padding:0; margin-left:2px; }
        .icd-search-wrap { position:relative; }
        .icd-search-icon { position:absolute; left:9px; top:50%; transform:translateY(-50%); color:#9ca3af; }
        .icd-search-input { width:100%; box-sizing:border-box; padding:7px 10px 7px 28px; border:1px solid #c9cfd8; border-radius:5px; font-size:13px; }
        .icd-dropdown { position:absolute; top:100%; left:0; right:0; z-index:300; background:#fff; border:1px solid #d1d5db; border-radius:7px; box-shadow:0 4px 16px rgba(0,0,0,.12); margin-top:2px; max-height:220px; overflow-y:auto; }
        .icd-option { display:flex; align-items:center; gap:8px; padding:8px 12px; cursor:pointer; font-size:12px; }
        .icd-option:hover { background:#f0f9ff; }
        .icd-option code { font-size:11px; color:#0369a1; font-weight:700; background:#e0f2fe; padding:1px 5px; border-radius:3px; flex-shrink:0; }
        .icd-option span { color:#374151; }

        /* ── Odontogram ── */
        .odontogram-wrap {
          background: #f8fafc; border: 1px solid #e5e7eb; border-radius: 12px;
          padding: 20px 16px 14px; overflow-x: auto;
        }
        .odo-chart { display: flex; flex-direction: column; align-items: center; gap: 0; width: 716px; min-width: 716px; margin: 0 auto; }
        .odo-quadrant { display: flex; flex-direction: column; width: 100%; }
        .odo-half-row { display: flex; align-items: flex-end; justify-content: flex-start; gap: 2px; margin-bottom: 2px; width: 716px; }
        .odo-half-row.odo-deciduous-row { margin-bottom: 0; }
        .odo-half-row.odo-deciduous-row .odo-cell { justify-content: flex-end; }
        .odo-quadrant:last-child .odo-half-row.odo-deciduous-row .odo-cell { justify-content: flex-start; }
        .odo-deciduous-offset { width: 168px; flex-shrink: 0; }
        .odo-midgap { width: 16px; flex-shrink: 0; }
        .odo-midline { height: 2px; background: linear-gradient(90deg,transparent,#94a3b8 20%,#94a3b8 80%,transparent); margin: 2px 0; width: 100%; border-radius: 2px; align-self: center; }
        .odo-row { display: flex; gap: 2px; }
        .odo-cell { display: flex; flex-direction: column; align-items: center; gap: 2px; position: relative; }
        .odo-cell-click { cursor: pointer; }
        .odo-cell-click:hover svg rect:first-child { stroke: #16a34a !important; fill: #f0fdf4 !important; }
        .odo-cell-filled svg rect:first-child { filter: drop-shadow(0 0 2px var(--tc)); }
        .odo-num { font-size: 9.5px; color: #9ca3af; font-weight: 700; line-height: 1; letter-spacing: -.3px; font-variant-numeric: tabular-nums; }
        .odo-cell-filled .odo-num { color: var(--tc); }
        .odo-legend { display: flex; flex-wrap: wrap; gap: 6px 10px; margin-top: 14px; padding-top: 12px; border-top: 1px solid #e5e7eb; justify-content: center; }
        .odo-leg-item { display: flex; align-items: center; gap: 4px; font-size: 10px; color: #374151; font-weight: 600; }
        .odo-leg-dot { width: 10px; height: 10px; border-radius: 3px; display: inline-block; flex-shrink: 0; }

        /* ── Tooth Modal ── */
        .tm-box { background: #fff; border-radius: 16px; width: 520px; max-width: 96vw; max-height: 88vh; display: flex; flex-direction: column; box-shadow: 0 20px 60px rgba(0,0,0,.18); overflow: hidden; }
        .tm-header { display: flex; align-items: center; justify-content: space-between; padding: 16px 18px 12px; border-bottom: 1px solid #f1f5f9; background: #f8fafc; flex-shrink: 0; }
        .tm-header-left { display: flex; align-items: center; gap: 12px; }
        .tm-tooth-preview { width: 52px; height: 52px; border-radius: 10px; border: 2px solid; display: flex; align-items: center; justify-content: center; }
        .tm-title { font-size: 14px; font-weight: 700; color: #111827; }
        .tm-subtitle { font-size: 12px; color: #6b7280; display: flex; align-items: center; gap: 6px; }
        .tm-type-badge { font-size: 10px; font-weight: 700; padding: 1px 6px; border-radius: 3px; background: #e0f2fe; color: #0369a1; }
        .tm-close { background: none; border: none; cursor: pointer; color: #9ca3af; padding: 4px; border-radius: 6px; display: flex; }
        .tm-close:hover { background: #fee2e2; color: #dc2626; }
        .tm-body { flex: 1; overflow-y: auto; padding: 14px 18px; display: flex; flex-direction: column; gap: 10px; }
        .tm-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px 14px; display: flex; flex-direction: column; gap: 8px; }
        .tm-card-head { display: flex; align-items: center; justify-content: space-between; }
        .tm-card-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
        .tm-field { display: flex; flex-direction: column; gap: 4px; }
        .tm-field label { font-size: 12px; font-weight: 600; color: #374151; }
        .tm-req { color: #dc2626; }
        .tm-select, .tm-input { padding: 7px 10px; border: 1px solid #d1d5db; border-radius: 7px; font-size: 13px; background: #fff; width: 100%; box-sizing: border-box; }
        .tm-select:focus, .tm-input:focus { outline: none; border-color: #16a34a; box-shadow: 0 0 0 2px rgba(22,163,74,.15); }
        .tm-surface { font-weight: 600; }
        .tm-remove { display: inline-flex; align-items: center; gap: 4px; padding: 4px 9px; border: 1px solid #fecaca; border-radius: 5px; background: #fff; color: #dc2626; font-size: 11px; cursor: pointer; }
        .tm-remove:hover { background: #fee2e2; }
        .tm-add-surface { align-self: flex-start; display: inline-flex; align-items: center; gap: 5px; padding: 7px 14px; border: 1.5px dashed #0369a1; border-radius: 7px; background: #f0f9ff; color: #0369a1; font-size: 12px; font-weight: 600; cursor: pointer; }
        .tm-add-surface:hover { background: #e0f2fe; }
        .tm-footer { display: flex; justify-content: flex-end; gap: 8px; padding: 12px 18px; border-top: 1px solid #e5e7eb; background: #f8fafc; flex-shrink: 0; }
        .tm-btn-cancel { padding: 8px 16px; border: 1px solid #d1d5db; border-radius: 7px; background: #fff; color: #374151; font-size: 13px; font-weight: 600; cursor: pointer; }
        .tm-btn-clear  { padding: 8px 16px; border: 1px solid #fecaca; border-radius: 7px; background: #fff; color: #dc2626; font-size: 13px; font-weight: 600; cursor: pointer; }
        .tm-btn-clear:hover  { background: #fee2e2; }
        .tm-btn-save   { padding: 8px 20px; border: none; border-radius: 7px; background: #16a34a; color: #fff; font-size: 13px; font-weight: 700; cursor: pointer; }
        .tm-btn-save:hover   { background: #15803d; }
        .tm-btn-cancel:hover { background: #f1f5f9; }
      `}</style>
    </div>
  );
}
